import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

// Shared filter shape every Session Analytics page sends: range (today/
// yesterday/7d/30d/custom) + from/to (only used when range==="custom") +
// optional session type. Matches backend's helpers/analytics/dateRange.js.
export const analyticsApi = createApi({
  reducerPath: "analyticsApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // Overview
    getOverviewKpis: builder.query({
      query: ({ range, from, to, type }) => ({
        url: "analytics/overview/kpis",
        method: "GET",
        params: { range, from, to, type },
      }),
    }),
    getLiveNow: builder.query({
      query: () => ({ url: "analytics/overview/live-now", method: "GET" }),
    }),
    getSessionsOverTime: builder.query({
      query: ({ range, from, to, type, metric, groupBy }) => ({
        url: "analytics/overview/sessions-over-time",
        method: "GET",
        params: { range, from, to, type, metric, groupBy },
      }),
    }),
    getSessionBreakdown: builder.query({
      query: ({ range, from, to }) => ({
        url: "analytics/overview/session-breakdown",
        method: "GET",
        params: { range, from, to },
      }),
    }),

    // Users
    getUserKpis: builder.query({
      query: ({ range, from, to }) => ({
        url: "analytics/users/kpis",
        method: "GET",
        params: { range, from, to },
      }),
    }),
    getTopUsers: builder.query({
      query: ({ range, from, to, sort, page, limit, search }) => ({
        url: "analytics/users/top",
        method: "GET",
        params: { range, from, to, sort, page, limit, search },
      }),
    }),
    getUserSummary: builder.query({
      query: ({ userId, range, from, to }) => ({
        url: `analytics/users/${userId}/summary`,
        method: "GET",
        params: { range, from, to },
      }),
    }),
    getUserListenerConnections: builder.query({
      query: ({ userId, page, limit, sort }) => ({
        url: `analytics/users/${userId}/listener-connections`,
        method: "GET",
        params: { page, limit, sort },
      }),
    }),

    // Listeners
    getListenerKpis: builder.query({
      query: ({ range, from, to }) => ({
        url: "analytics/listeners/kpis",
        method: "GET",
        params: { range, from, to },
      }),
    }),
    getTopListeners: builder.query({
      query: ({ range, from, to, sort, page, limit, search }) => ({
        url: "analytics/listeners/top",
        method: "GET",
        params: { range, from, to, sort, page, limit, search },
      }),
    }),
    getListenerSummary: builder.query({
      query: ({ listenerId, range, from, to }) => ({
        url: `analytics/listeners/${listenerId}/summary`,
        method: "GET",
        params: { range, from, to },
      }),
    }),
    getListenerUserConnections: builder.query({
      query: ({ listenerId, page, limit, sort }) => ({
        url: `analytics/listeners/${listenerId}/user-connections`,
        method: "GET",
        params: { page, limit, sort },
      }),
    }),

    // Inactive
    getInactiveUsers: builder.query({
      query: ({ bucket, page, limit, search }) => ({
        url: "analytics/inactive-users",
        method: "GET",
        params: { bucket, page, limit, search },
      }),
    }),
    getInactiveListeners: builder.query({
      query: ({ bucket, page, limit, search }) => ({
        url: "analytics/inactive-listeners",
        method: "GET",
        params: { bucket, page, limit, search },
      }),
    }),

    // Peak hours
    getPeakHours: builder.query({
      query: ({ range, from, to, type }) => ({
        url: "analytics/peak-hours",
        method: "GET",
        params: { range, from, to, type },
      }),
    }),
    getListenerAvailabilityHeatmap: builder.query({
      query: ({ range, from, to, limit }) => ({
        url: "analytics/listeners/availability-heatmap",
        method: "GET",
        params: { range, from, to, limit },
      }),
    }),

    // Phase 2: commitment/score, trends, insights
    getListenerCommitment: builder.query({
      query: ({ listenerId, range, from, to }) => ({
        url: `analytics/listeners/${listenerId}/commitment`,
        method: "GET",
        params: { range, from, to },
      }),
    }),
    getListenerEarningsTrend: builder.query({
      query: ({ listenerId, range, from, to }) => ({
        url: `analytics/listeners/${listenerId}/earnings-trend`,
        method: "GET",
        params: { range, from, to },
      }),
    }),
    getUserSpendingTrend: builder.query({
      query: ({ userId, range, from, to }) => ({
        url: `analytics/users/${userId}/spending-trend`,
        method: "GET",
        params: { range, from, to },
      }),
    }),
    getUserOnlineActivity: builder.query({
      query: ({ range, from, to, page, limit, search }) => ({
        url: "analytics/users/online-activity",
        method: "GET",
        params: { range, from, to, page, limit, search },
      }),
    }),
    getConnectionInsights: builder.query({
      query: ({ range, from, to }) => ({
        url: "analytics/connection-insights",
        method: "GET",
        params: { range, from, to },
      }),
    }),
  }),
});

export const {
  useGetOverviewKpisQuery,
  useGetLiveNowQuery,
  useGetSessionsOverTimeQuery,
  useGetSessionBreakdownQuery,
  useGetUserKpisQuery,
  useGetTopUsersQuery,
  useGetUserSummaryQuery,
  useGetUserListenerConnectionsQuery,
  useGetListenerKpisQuery,
  useGetTopListenersQuery,
  useGetListenerSummaryQuery,
  useGetListenerUserConnectionsQuery,
  useGetInactiveUsersQuery,
  useGetInactiveListenersQuery,
  useGetPeakHoursQuery,
  useGetListenerAvailabilityHeatmapQuery,
  useGetListenerCommitmentQuery,
  useGetListenerEarningsTrendQuery,
  useGetUserSpendingTrendQuery,
  useGetUserOnlineActivityQuery,
  useGetConnectionInsightsQuery,
} = analyticsApi;
